/**
 * Project Hooks — 프로젝트 상태 관리 및 실시간 업데이트
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase, apiFetch } from '../lib/supabase';

// ============================================================
// Types
// ============================================================

interface Project {
  id: string;
  url: string;
  name: string;
  status: string;
  preferences: {
    video_length: string;
    language: string;
    tone: string;
    include_narration: boolean;
    include_bgm: boolean;
    output_formats: string[];
    resolution: string;
  };
  created_at: string;
  updated_at: string;
}

interface PipelineStep {
  id: string;
  step_name: string;
  agent: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  error_message: string | null;
}

interface PipelineStatus {
  project_id: string;
  status: string;
  progress: number;
  steps: PipelineStep[];
  video_url?: string;
  seedance_status?: string;
  updated_at: string;
}

// ============================================================
// useProjects — 프로젝트 목록 조회
// ============================================================

export function useProjects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiFetch('/api/projects');
      setProjects(data.projects || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const createProject = async (params: { url: string; name: string; preferences?: any }) => {
    const data = await apiFetch('/api/projects', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    await fetchProjects();
    return data.project;
  };

  const deleteProject = async (id: string) => {
    await apiFetch(`/api/projects/${id}`, { method: 'DELETE' });
    await fetchProjects();
  };

  return { projects, loading, error, createProject, deleteProject, refresh: fetchProjects };
}

// ============================================================
// usePipeline — 파이프라인 실시간 상태
// ============================================================

export function usePipeline(projectId: string | null) {
  const [status, setStatus] = useState<PipelineStatus | null>(null);
  const [loading, setLoading] = useState(false);

  // 상태 폴링
  useEffect(() => {
    if (!projectId) return;

    let active = true;
    const poll = async () => {
      try {
        const data = await apiFetch(`/api/pipeline/${projectId}/status`);
        if (active) setStatus(data);
      } catch { /* ignore */ }
    };

    poll();
    const interval = setInterval(poll, 3000); // 3초마다 폴링

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [projectId]);

  // Supabase Realtime 구독
  useEffect(() => {
    if (!projectId) return;

    const channel = supabase
      .channel(`pipeline:${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pipeline_steps',
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          // 실시간 업데이트 반영
          setStatus(prev => {
            if (!prev) return prev;
            const updatedStep = payload.new as PipelineStep;
            const steps = prev.steps.map(s =>
              s.id === updatedStep.id ? updatedStep : s
            );
            const completedCount = steps.filter(s => s.status === 'completed').length;
            return {
              ...prev,
              steps,
              progress: Math.round((completedCount / 6) * 100),
            };
          });
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [projectId]);

  const startPipeline = async (overrideId?: string) => {
    const id = overrideId || projectId;
    if (!id || loading) return;
    setLoading(true);
    try {
      await apiFetch(`/api/pipeline/${id}/start`, { method: 'POST' });
    } catch {
      // 409 등 에러 무시 (이미 실행 중)
    } finally {
      setLoading(false);
    }
  };

  const approveScenario = async (approved: boolean, notes?: string) => {
    if (!projectId) return;
    await apiFetch(`/api/pipeline/${projectId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approved, notes }),
    });
  };

  const stopPipeline = async () => {
    if (!projectId) return;
    await apiFetch(`/api/pipeline/${projectId}/stop`, { method: 'POST' });
  };

  return { status, loading, startPipeline, approveScenario, stopPipeline };
}
